//--- React
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
//--- Ant Design
import { Row, Col, Tabs, Modal } from "antd";
//--- Project
import { getApis, postApis } from "components/Common/apis";
import {
  vehicleDetailsApiUrl,
  // imeiListApiUrl,
  vehicleTestStopApiUrl,
  vehicleTestStartApiUrl,
} from "components/Common/endpoints";
import {
  setVehicleDetails,
  // setImeiList,
  setHBResultDetails,
  setHBStartDetails,
  setDynoTestRunningStatus,
  setSelectedTab,
  setDynoStartDetails,
  setDynoStopDetails,
  setHBTestRunningStatus,
  setDynoPaginationDetails,
  setHBPaginationDetails,
  setHBPageMini,
} from "reducers/eolSlice";
import { setNavStop } from "reducers/authSlice";
import { setPageLoader } from "reducers/loaderSlice";
import { useRequiredAccess } from "hooks/useRequiredAccess";
import { ORG_TYPES, ROLES } from "components/Common/Constants";
import { EOLConfirmationPopup } from "components/Common/confirmationPopup";
import Connectivity from "./Connectivity";
import Dyno from "./Dyno";
import DynoStartTest from "./Dyno/Panels/StartTest";
import HBStartTest from "./Connectivity/Panels/HBStartTest";

const EolTests = () => {
  const dispatch = useDispatch();
  const navStop = useSelector((state) => state.auth)?.navStop;
  const eol = useSelector((state) => state.eol);
  const vehicleDetails = eol?.vehicleDetails;
  const selectedTab = eol?.selectedTab;
  const dynoStartDetails = eol?.dynoStartDetails;
  const HBTestRunningStatus = eol?.HBTestRunningStatus;
  const dynoTestRunningStatus = eol?.dynoTestRunningStatus;
  // const [imei, setImei] = useState(eol?.vehicleDetails?.imeiNumber || "");
  const [vehicle, setVehicle] = useState({
    vehId: eol?.vehicleDetails?.vehId || null,
    vehImei: eol?.vehicleDetails?.imeiNumber || null,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modal, contextHolder] = Modal.useModal();

  const authToken = localStorage.getItem("authToken");
  const checkAccess = useRequiredAccess(
    [ROLES.ADMIN, ROLES.EXECUTIVE, ROLES.TEST_ENGINEER],
    [ORG_TYPES.ADMINISTRATOR, ORG_TYPES.MANUFACTURER]
  );
  const queryClient = useQueryClient();

  const showCommentModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // const getImeiList = useCallback(async () => {
  //   await getApis(imeiListApiUrl, {}, authToken)
  //     .then((response) => {
  //       dispatch(setImeiList(response?.data));
  //       setImei(response?.data[0]);
  //     })
  //     .catch((e) => toast.error(e.response?.data?.message));
  // }, [authToken, dispatch]);

  const getVehicleDetails = useCallback(async () => {
    await getApis(
      vehicleDetailsApiUrl,
      {
        imei: vehicle?.vehImei,
      },
      authToken
    )
      .then((response) => {
        dispatch(setVehicleDetails(response?.data));
        queryClient.invalidateQueries("hbtests", "hbresult");
      })
      .catch((e) => toast.error(e.response?.data?.message));
    dispatch(setPageLoader(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, dispatch, vehicle?.vehId]);

  // useEffect(() => {
  //   if (eol?.imeiList?.length === 0) {
  //     getImeiList();
  //   } else {
  //     setImei(eol?.vehicleDetails?.imeiNumber );
  //   }
  //   dispatch(setPageLoader("refresh"));

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [dispatch, getImeiList]);

  useEffect(() => {
    checkAccess();
    if (vehicle?.vehImei && vehicle.vehImei !== "") {
      dispatch(setPageLoader("refresh"));
      getVehicleDetails();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getVehicleDetails, vehicle?.vehImei, dispatch]);

  const onChangeImei = async (_, value) => {
    const confirmation = navStop
      ? await modal.confirm(EOLConfirmationPopup)
      : true;
    if (confirmation) {
      dispatch(
        setHBPaginationDetails({
          current: 1,
        })
      );
      dispatch(setHBPageMini(1));
      dispatch(
        setDynoPaginationDetails({
          current: 1,
        })
      );
      setVehicle(value);
      // dispatch(setVehicleDetails({}));
      dispatch(setHBStartDetails({}));
      dispatch(setHBResultDetails({}));
      dispatch(setHBTestRunningStatus(false));
      dispatch(setNavStop(false));
      dispatch(setDynoStartDetails({}));
      dispatch(setDynoTestRunningStatus(false));
      dispatch(setDynoStopDetails({}));
    }
  };

  const onChangeTab = async (key) => {
    const confirmation = navStop
      ? await modal.confirm(EOLConfirmationPopup)
      : true;
    if (confirmation) {
      dispatch(setSelectedTab(key));

      if (
        (dynoTestRunningStatus && key === "dyno") ||
        (HBTestRunningStatus && key === "heartBeat")
      ) {
        dispatch(setNavStop(true));
      } else {
        dispatch(setNavStop(false));
      }
    }
  };

  const dynoStartTest = async () => {
    await postApis(
      vehicleTestStartApiUrl,
      {
        imei: vehicleDetails?.imeiNumber,
        startTime: Date.now(),
        type: "DYNO",
      },
      authToken
    )
      .then((response) => {
        dispatch(setDynoStartDetails(response?.data));
        dispatch(setDynoTestRunningStatus(true));
        dispatch(setNavStop(true));
        dispatch(setDynoStopDetails({}));
      })
      .catch((e) => toast.error(e.response?.data?.message));
  };

  const dynoStopTest = async () => {
    await postApis(
      vehicleTestStopApiUrl,
      {
        imei: vehicleDetails?.imeiNumber,
        stopTime: Date.now(),
        testId: dynoStartDetails?.testId || vehicleDetails?.previousTests?.dyno,
      },
      authToken
    )
      .then((response) => {
        // toast.success("Test successfully completed");
        dispatch(setDynoStopDetails(response?.data));
        dispatch(setDynoTestRunningStatus(false));
        dispatch(setNavStop(false));
        if (
          response?.data?.status === "COMPLETED" &&
          !response?.data?.comment
        ) {
          showCommentModal();
        }
      })
      .catch((e) => toast.error(e.response?.data?.message));
  };

  const items = [
    {
      key: "heartBeat",
      label: `Heart Beat`,
      children: <Connectivity onChangeImei={onChangeImei} vehicle={vehicle} />,
    },
    {
      key: "dyno",
      label: `Dyno`,
      children: (
        <Dyno
          isModalOpen={isModalOpen}
          showCommentModal={showCommentModal}
          handleCloseModal={handleCloseModal}
          onChangeImei={onChangeImei}
          vehicle={vehicle}
        />
      ),
    },
  ];

  return (
    <Row gutter={16} data-testid="eol-tests">
      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
        <Tabs
          defaultActiveKey={selectedTab}
          items={items}
          activeKey={selectedTab}
          onChange={onChangeTab}
          tabBarExtraContent={
            selectedTab === "dyno" ? (
              <DynoStartTest
                stopTest={dynoStopTest}
                startTest={dynoStartTest}
              />
            ) : (
              <HBStartTest />
            )
          }
        />
      </Col>
      {contextHolder}
    </Row>
  );
};

export default EolTests;
